import type { EvaluationResult, MeshBody } from "../model/types";

export type PrintIssue = { severity: "info" | "warning" | "error"; title: string; detail: string };
export type PrintReport = {
  dimensions: [number, number, number];
  volume: number;
  area: number;
  triangles: number;
  bodyCount: number;
  issues: PrintIssue[];
};

const WELD_TOLERANCE_MM = 1e-5;
const VERY_SMALL_EXTENT_MM = 0.8;

function weldVertexIndices(vertices: Float32Array): Uint32Array {
  const weldedByKey = new Map<string, number>();
  const mapped = new Uint32Array(vertices.length / 3);
  let next = 0;
  for (let index = 0; index < mapped.length; index += 1) {
    const x = vertices[index * 3] ?? 0;
    const y = vertices[index * 3 + 1] ?? 0;
    const z = vertices[index * 3 + 2] ?? 0;
    const key = `${Math.round(x / WELD_TOLERANCE_MM)}:${Math.round(y / WELD_TOLERANCE_MM)}:${Math.round(z / WELD_TOLERANCE_MM)}`;
    let welded = weldedByKey.get(key);
    if (welded === undefined) {
      welded = next;
      next += 1;
      weldedByKey.set(key, welded);
    }
    mapped[index] = welded;
  }
  return mapped;
}

function countTriangleComponents(indices: Uint32Array, mapped: Uint32Array): number {
  const triangleCount = Math.floor(indices.length / 3);
  if (triangleCount === 0) return 0;
  const trianglesByVertex = new Map<number, number[]>();
  for (let triangle = 0; triangle < triangleCount; triangle += 1) {
    for (let corner = 0; corner < 3; corner += 1) {
      const original = indices[triangle * 3 + corner] ?? 0;
      const vertex = mapped[original] ?? original;
      const list = trianglesByVertex.get(vertex) ?? [];
      list.push(triangle);
      trianglesByVertex.set(vertex, list);
    }
  }

  const visited = new Uint8Array(triangleCount);
  let components = 0;
  for (let start = 0; start < triangleCount; start += 1) {
    if (visited[start]) continue;
    components += 1;
    const stack = [start];
    visited[start] = 1;
    while (stack.length > 0) {
      const triangle = stack.pop();
      if (triangle === undefined) break;
      for (let corner = 0; corner < 3; corner += 1) {
        const original = indices[triangle * 3 + corner] ?? 0;
        const vertex = mapped[original] ?? original;
        for (const neighbour of trianglesByVertex.get(vertex) ?? []) {
          if (visited[neighbour]) continue;
          visited[neighbour] = 1;
          stack.push(neighbour);
        }
      }
    }
  }
  return components;
}

function analyseBody(body: MeshBody): PrintIssue[] {
  const issues: PrintIssue[] = [];
  const edgeCounts = new Map<string, number>();
  const vertices = body.vertices;
  const indices = body.triangles;
  const welded = weldVertexIndices(vertices);
  let degenerate = 0;
  let signedVolumeTimesSix = 0;

  const edge = (originalA: number, originalB: number): void => {
    const a = welded[originalA] ?? originalA;
    const b = welded[originalB] ?? originalB;
    const key = a < b ? `${a}:${b}` : `${b}:${a}`;
    edgeCounts.set(key, (edgeCounts.get(key) ?? 0) + 1);
  };

  for (let index = 0; index < indices.length; index += 3) {
    const a = indices[index] ?? 0;
    const b = indices[index + 1] ?? 0;
    const c = indices[index + 2] ?? 0;
    edge(a, b);
    edge(b, c);
    edge(c, a);

    const ax = vertices[a * 3] ?? 0;
    const ay = vertices[a * 3 + 1] ?? 0;
    const az = vertices[a * 3 + 2] ?? 0;
    const bx = vertices[b * 3] ?? 0;
    const by = vertices[b * 3 + 1] ?? 0;
    const bz = vertices[b * 3 + 2] ?? 0;
    const cx = vertices[c * 3] ?? 0;
    const cy = vertices[c * 3 + 1] ?? 0;
    const cz = vertices[c * 3 + 2] ?? 0;
    const abx = bx - ax;
    const aby = by - ay;
    const abz = bz - az;
    const acx = cx - ax;
    const acy = cy - ay;
    const acz = cz - az;
    const crossX = aby * acz - abz * acy;
    const crossY = abz * acx - abx * acz;
    const crossZ = abx * acy - aby * acx;
    if (Math.hypot(crossX, crossY, crossZ) < 1e-8) degenerate += 1;
    signedVolumeTimesSix += ax * (by * cz - bz * cy) - ay * (bx * cz - bz * cx) + az * (bx * cy - by * cx);
  }

  const boundary = [...edgeCounts.values()].filter((count) => count === 1).length;
  const nonManifold = [...edgeCounts.values()].filter((count) => count > 2).length;
  const components = countTriangleComponents(indices, welded);
  const extents = body.bounds.max.map((value, axis) => value - (body.bounds.min[axis] ?? 0));

  if (boundary > 0) issues.push({ severity: "error", title: "Open mesh", detail: `${boundary} boundary edges were found in ${body.name}.` });
  if (nonManifold > 0) issues.push({ severity: "error", title: "Non-manifold mesh", detail: `${nonManifold} edges are shared by more than two triangles in ${body.name}.` });
  if (degenerate > 0) issues.push({ severity: "warning", title: "Degenerate triangles", detail: `${degenerate} zero-area triangles were detected in ${body.name}.` });
  if (components > 1) issues.push({ severity: "warning", title: "Disconnected shells", detail: `${body.name} contains ${components} disconnected mesh components.` });
  if (body.volume <= 0) issues.push({ severity: "error", title: "No enclosed volume", detail: `${body.name} does not report a positive solid volume.` });
  if (signedVolumeTimesSix < -1e-6) issues.push({ severity: "warning", title: "Inverted mesh orientation", detail: `${body.name} appears to have inward-facing triangle orientation.` });
  const smallestExtent = Math.min(...extents);
  if (smallestExtent > 0 && smallestExtent < VERY_SMALL_EXTENT_MM) {
    issues.push({ severity: "warning", title: "Very thin extent", detail: `${body.name} is only ${smallestExtent.toFixed(2)} mm thick on one axis. Check your printer and material limits.` });
  }
  return issues;
}

export function createPrintReport(evaluation: EvaluationResult | null, printer: [number, number, number] = [220, 220, 250]): PrintReport | null {
  if (!evaluation || evaluation.bodies.length === 0) return null;
  const minimum: [number, number, number] = [Infinity, Infinity, Infinity];
  const maximum: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  let volume = 0;
  let area = 0;
  let triangles = 0;
  const issues: PrintIssue[] = [];
  for (const body of evaluation.bodies) {
    for (let axis = 0; axis < 3; axis += 1) {
      minimum[axis] = Math.min(minimum[axis] ?? Infinity, body.bounds.min[axis] ?? 0);
      maximum[axis] = Math.max(maximum[axis] ?? -Infinity, body.bounds.max[axis] ?? 0);
    }
    volume += body.volume;
    area += body.area;
    triangles += body.triangles.length / 3;
    issues.push(...analyseBody(body));
  }
  const dimensions: [number, number, number] = [maximum[0] - minimum[0], maximum[1] - minimum[1], maximum[2] - minimum[2]];
  if (dimensions.some((dimension, index) => dimension > (printer[index] ?? Infinity))) {
    issues.push({ severity: "warning", title: "Outside printer volume", detail: `The model is ${dimensions.map((value) => value.toFixed(1)).join(" × ")} mm; the selected printer is ${printer.join(" × ")} mm.` });
  }
  if (evaluation.bodies.length > 1) issues.push({ severity: "info", title: "Multiple bodies", detail: `${evaluation.bodies.length} separate bodies will be exported.` });
  if (issues.length === 0) issues.push({ severity: "info", title: "Ready to slice", detail: "No obvious mesh problems were detected." });
  return { dimensions, volume, area, triangles, bodyCount: evaluation.bodies.length, issues };
}
